import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config';

function LoadingOverlay({ show, text = 'Submitting your enquiry...' }) {
  if (!show) return null;

  return (
    <div className="overlay-backdrop fixed inset-0 z-50 grid place-items-center" role="dialog" aria-modal="true" aria-label="Loading">
      <div className="dialog-surface mx-4 w-full max-w-sm p-6 text-center">
        <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-purple-300 border-t-purple-500" aria-busy="true" />
        <p className="font-medium text-text">{text}</p>
        <p className="mt-1 text-xs text-secondary-text">Please wait...</p>
      </div>
    </div>
  );
}

const BILLING_CYCLES = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'annual', label: 'Yearly' },
];

const normalizeSubscriptionTiers = (plans) => (
  Object.entries(plans || {})
    .map(([id, plan]) => ({
      id,
      name: plan?.label || `Tier ${id}`,
      range: [plan?.min_employees ?? 1, plan?.max_employees ?? null],
      pricing: {
        currency: plan?.pricing?.currency || 'AED',
        monthlyMinor: plan?.pricing?.monthly_minor ?? null,
        annualMinor: plan?.pricing?.annual_minor ?? null,
        annualOnly: Boolean(plan?.pricing?.annual_only),
        annualFormula: plan?.pricing?.annual_formula || '',
        annualBaseMinor: plan?.pricing?.annual_base_minor ?? null,
        annualIncrementPerEmployeeMinor: plan?.pricing?.annual_increment_per_employee_minor ?? null,
        supportedBillingCycles: plan?.pricing?.supported_billing_cycles || ['monthly', 'annual'],
      },
    }))
    .sort((a, b) => Number(a.id) - Number(b.id))
);

const formatPrice = (_currency, amountMinor) => {
  if (amountMinor === null || amountMinor === undefined) return null;
  const amountMajor = amountMinor / 100;
  return new Intl.NumberFormat('en-AE', {
    style: 'decimal',
    minimumFractionDigits: Number.isInteger(amountMajor) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amountMajor);
};

const formatEmployeeRange = ([min, max]) => (max ? `${min}-${max} employees` : `${min}+ employees`);
const formatPlanDisplayName = (item) => (item?.id === '6' ? 'Custom plan' : formatEmployeeRange(item.range));
const stripVisibleAedText = (value) => (value || '').replace(/\bAED\b\s*/gi, '');

const parsePositiveInteger = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const findTierById = (tiers, tierId) => tiers.find((item) => item.id === tierId) || null;

const getSupportedBillingCycles = (plan) => plan?.pricing?.supportedBillingCycles || [];

const getPreferredBillingCycle = (plan, preferredCycle = 'annual') => {
  const supportedBillingCycles = getSupportedBillingCycles(plan);
  if (supportedBillingCycles.includes(preferredCycle)) {
    return preferredCycle;
  }
  if (supportedBillingCycles.includes('annual')) {
    return 'annual';
  }
  return supportedBillingCycles[0] || 'annual';
};

const sanitizeEmployeeCountForTier = (plan, rawCount, fallbackCount) => {
  if (!plan) return fallbackCount ?? 1;

  const [minEmployees, maxEmployees] = plan.range;
  const parsedCount = parsePositiveInteger(rawCount);
  const fallbackParsedCount = parsePositiveInteger(fallbackCount);
  let nextCount = parsedCount ?? fallbackParsedCount ?? minEmployees;

  if (nextCount < minEmployees) {
    nextCount = minEmployees;
  }
  if (maxEmployees !== null && nextCount > maxEmployees) {
    nextCount = maxEmployees;
  }
  return nextCount;
};

const getTierSixAnnualMinor = (tier, employeeCount) => {
  if (!tier?.pricing?.annualBaseMinor || !tier?.pricing?.annualIncrementPerEmployeeMinor) {
    return null;
  }
  const extraEmployees = Math.max(0, Number(employeeCount) - 200);
  return tier.pricing.annualBaseMinor + (extraEmployees * tier.pricing.annualIncrementPerEmployeeMinor);
};

const getTierDisplayPrice = (tier, billingCycle, employeeCount) => {
  if (!tier) return null;
  if (tier.id === '6') {
    return {
      amount: formatPrice(tier.pricing.currency, getTierSixAnnualMinor(tier, employeeCount)),
      cadence: '/ year',
      secondary: stripVisibleAedText(tier.pricing.annualFormula),
    };
  }

  if (billingCycle === 'monthly') {
    return {
      amount: formatPrice(tier.pricing.currency, tier.pricing.monthlyMinor),
      cadence: '/ month',
      secondary: `${formatPrice(tier.pricing.currency, tier.pricing.annualMinor)} billed yearly`,
    };
  }

  return {
    amount: formatPrice(tier.pricing.currency, tier.pricing.annualMinor),
    cadence: '/ year',
    secondary: `${formatPrice(tier.pricing.currency, tier.pricing.monthlyMinor)} equivalent monthly`,
  };
};

export default function PaymentPage() {
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('');
  const [employeeCount, setEmployeeCount] = useState(1);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [fullName, setFullName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [designation, setDesignation] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const formRef = useRef(null);
  const fullNameInputRef = useRef(null);
  const initialSelectionRef = useRef(null);

  if (initialSelectionRef.current === null) {
    const searchParams = typeof window === 'undefined'
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search);
    initialSelectionRef.current = {
      tierId: searchParams.get('tier') || searchParams.get('plan') || searchParams.get('tier_id') || '',
      billingCycle: searchParams.get('billing') || searchParams.get('billing_cycle') || '',
      employeeCount: parsePositiveInteger(
        searchParams.get('employees') || searchParams.get('employee_count') || '',
      ),
    };
  }

  const tier = useMemo(
    () => subscriptionTiers.find((item) => item.id === selectedTier) || subscriptionTiers[0] || null,
    [selectedTier, subscriptionTiers]
  );

  const standardTiers = useMemo(
    () => subscriptionTiers.filter((item) => item.id !== '6'),
    [subscriptionTiers]
  );

  const customTier = useMemo(
    () => subscriptionTiers.find((item) => item.id === '6') || null,
    [subscriptionTiers]
  );

  const availableBillingCycles = useMemo(
    () => BILLING_CYCLES.filter((cycle) => getSupportedBillingCycles(tier).includes(cycle.id)),
    [tier]
  );

  const selectedPlanSummary = useMemo(() => {
    if (!tier) return null;
    return getTierDisplayPrice(tier, billingCycle, employeeCount);
  }, [billingCycle, employeeCount, tier]);

  const tierSixEstimate = useMemo(() => {
    if (!customTier) return null;
    return formatPrice(customTier.pricing.currency, getTierSixAnnualMinor(customTier, employeeCount));
  }, [customTier, employeeCount]);

  const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isPhone = (value) => /^[0-9]{6,15}$/.test(value);
  const withinRange = (count, [min, max]) => {
    if (count < min) return false;
    if (max !== null && count > max) return false;
    return true;
  };

  useEffect(() => {
    let ignore = false;

    async function loadPlans() {
      setTiersLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/subscribe/`);
        const nextTiers = normalizeSubscriptionTiers(response?.data?.plans);
        if (!ignore) {
          setSubscriptionTiers(nextTiers);
          if (nextTiers.length > 0) {
            const initialSelection = initialSelectionRef.current || {};
            const initialTier = findTierById(nextTiers, initialSelection.tierId) || nextTiers[0];
            setSelectedTier(initialTier.id);
            setBillingCycle(getPreferredBillingCycle(initialTier, initialSelection.billingCycle || 'annual'));
            setEmployeeCount(
              sanitizeEmployeeCountForTier(
                initialTier,
                initialSelection.employeeCount,
                initialTier.range[0],
              ),
            );
          }
        }
      } catch (err) {
        if (!ignore) {
          setError('Could not load available plans right now. Please try again shortly.');
        }
      } finally {
        if (!ignore) {
          setTiersLoading(false);
        }
      }
    }

    loadPlans();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!subscriptionTiers.length) return;

    const resolvedTier = tier || subscriptionTiers[0];
    if (!resolvedTier) return;

    if (!tier || selectedTier !== resolvedTier.id) {
      setSelectedTier(resolvedTier.id);
    }

    const preferredBillingCycle = getPreferredBillingCycle(resolvedTier, billingCycle);
    if (billingCycle !== preferredBillingCycle) {
      setBillingCycle(preferredBillingCycle);
    }

    const sanitizedEmployeeCount = sanitizeEmployeeCountForTier(
      resolvedTier,
      employeeCount,
      resolvedTier.range[0],
    );
    if (Number(employeeCount) !== sanitizedEmployeeCount) {
      setEmployeeCount(sanitizedEmployeeCount);
    }
  }, [billingCycle, employeeCount, selectedTier, subscriptionTiers, tier]);

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      fullNameInputRef.current?.focus();
    }, 250);
  }, []);

  const applyPlanSelection = useCallback((nextTierId, options = {}) => {
    const nextTier = findTierById(subscriptionTiers, nextTierId);
    if (!nextTier) return;

    const {
      nextBillingCycle,
      nextEmployeeCount,
      preserveCurrentEmployeeCount = false,
    } = options;

    setSelectedTier(nextTier.id);
    setBillingCycle(getPreferredBillingCycle(nextTier, nextBillingCycle || billingCycle));
    setEmployeeCount(
      sanitizeEmployeeCountForTier(
        nextTier,
        nextEmployeeCount,
        preserveCurrentEmployeeCount ? employeeCount : nextTier.range[0],
      ),
    );
  }, [billingCycle, employeeCount, subscriptionTiers]);

  const selectPlan = useCallback((nextTierId, nextEmployeeCount, nextBillingCycle) => {
    applyPlanSelection(nextTierId, {
      nextBillingCycle,
      nextEmployeeCount,
    });
    setError(null);
    setOk(null);
    scrollToForm();
  }, [applyPlanSelection, scrollToForm]);

  const handleTierChange = useCallback((event) => {
    applyPlanSelection(event.target.value, {
      preserveCurrentEmployeeCount: true,
    });
  }, [applyPlanSelection]);

  const handleBillingChange = useCallback((event) => {
    const nextBillingCycle = event.target.value;
    if (!tier) return;
    setBillingCycle(getPreferredBillingCycle(tier, nextBillingCycle));
  }, [tier]);

  const handleEmployeeCountChange = useCallback((event) => {
    const parsedCount = parsePositiveInteger(event.target.value);
    setEmployeeCount(parsedCount ?? '');
  }, []);

  const handleEmployeeCountBlur = useCallback(() => {
    if (!tier) return;
    setEmployeeCount(
      sanitizeEmployeeCountForTier(tier, employeeCount, tier.range[0]),
    );
  }, [employeeCount, tier]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setOk(null);

    if (!fullName.trim()) return setError('Please enter your full name.');
    if (!organisation.trim()) return setError('Please enter your organisation.');
    if (!isPhone(phone)) return setError('Please enter a valid phone number (digits only).');
    if (!isEmail(email.trim().toLowerCase())) return setError('Please enter a valid email.');
    if (!tier) return setError('Subscription plans are still loading. Please wait a moment.');
    if (!withinRange(Number(employeeCount), tier.range)) {
      return setError(`Employee count must be within ${tier.range[0]}${tier.range[1] ? `-${tier.range[1]}` : '+'}.`);
    }

    setIsLoading(true);
    try {
      const payload = {
        full_name: fullName.trim(),
        organisation: organisation.trim(),
        designation: designation.trim() || null,
        email: email.trim().toLowerCase(),
        phone_cc: countryCode,
        phone_number: phone,
        tier_id: tier.id,
        employee_count: Number(employeeCount),
        billing_cycle: billingCycle,
      };

      await axios.post(`${API_URL}/api/plan-enquiry/`, payload);
      setOk('Thanks! Our team will contact you shortly.');
      setFullName('');
      setOrganisation('');
      setDesignation('');
      setPhone('');
      setEmail('');
      if (subscriptionTiers[0]) {
        setSelectedTier(subscriptionTiers[0].id);
        setEmployeeCount(subscriptionTiers[0].range[0]);
      }
      setBillingCycle('annual');
    } catch (err) {
      const message = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Unknown error';
      setError(`Could not submit: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }, [billingCycle, countryCode, designation, email, employeeCount, fullName, organisation, phone, subscriptionTiers, tier]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background pb-16">
      <LoadingOverlay show={isLoading} text="Submitting your enquiry..." />

      <section className="relative overflow-hidden border-b border-border bg-white/80">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-[radial-gradient(circle_at_top,rgba(168,85,247,0.24),transparent_58%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-4 inline-flex items-center rounded-full border border-border bg-white/90 px-4 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-secondary-text shadow-soft">
              PutPens pricing
            </div>
            <h1 className="text-4xl font-extrabold tracking-tight text-text sm:text-5xl">
              Choose the right plan for your team
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-secondary-text sm:text-lg">
              Flexible PutPens plans designed for teams of every size.
            </p>

            <div className="mt-8 inline-flex rounded-full border border-border bg-white p-1 shadow-soft">
              {BILLING_CYCLES.map((cycle) => {
                const isActive = billingCycle === cycle.id && !(tier?.id === '6' && cycle.id === 'monthly');
                return (
                  <button
                    key={cycle.id}
                    type="button"
                    onClick={() => setBillingCycle(cycle.id)}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'bg-primary text-white shadow-btn'
                        : 'text-secondary-text hover:text-text'
                    }`}
                  >
                    {cycle.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
        <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-5">
          {standardTiers.map((item) => {
            const display = getTierDisplayPrice(item, billingCycle, employeeCount);
            const isSelected = selectedTier === item.id;
            return (
              <article
                key={item.id}
                className={`card relative flex h-full flex-col p-6 transition duration-200 hover:-translate-y-1 hover:shadow-soft ${
                  isSelected ? 'ring-2 ring-primary/30' : ''
                }`}
              >
                <div className="mb-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-secondary-text">
                    {formatPlanDisplayName(item)}
                  </p>
                  <p className="mt-2 text-sm text-secondary-text">{formatEmployeeRange(item.range)}</p>
                </div>

                <div className="mb-5">
                  <div className="flex items-end gap-2">
                    <span className="text-4xl font-extrabold tracking-tight text-text">{display?.amount}</span>
                    <span className="pb-1 text-sm font-medium text-secondary-text">{display?.cadence}</span>
                  </div>
                  <p className="mt-3 min-h-[2.75rem] text-sm text-secondary-text">{display?.secondary}</p>
                </div>

                <div className="mt-auto">
                  <button
                    type="button"
                    onClick={() => selectPlan(item.id, item.range[0], billingCycle)}
                    className="primary-btn w-full justify-center py-3"
                  >
                    Select Plan
                  </button>
                </div>
              </article>
            );
          })}
        </section>

        {customTier && (
          <section className="mt-8">
            <div className="card overflow-hidden">
              <div className="grid gap-0 lg:grid-cols-[1.25fr_0.95fr]">
                <div className="border-b border-border bg-white p-6 sm:p-8 lg:border-b-0 lg:border-r">
                  <div className="mb-5 inline-flex rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-secondary-text">
                    Custom plan
                  </div>
                  <h2 className="text-3xl font-bold tracking-tight text-text">For teams above 200 employees</h2>
                  <p className="mt-3 max-w-2xl text-base text-secondary-text">
                    Built for larger operations that need PutPens across a broader workforce without losing clarity in planning, approvals, and accountability.
                  </p>

                  <div className="mt-6 rounded-[1.75rem] border border-border bg-background/70 p-5">
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-secondary-text">Yearly pricing</p>
                    <p className="mt-3 text-3xl font-extrabold text-text">6,000/year</p>
                    <p className="mt-2 text-sm text-secondary-text">2.5 for each additional employee above 200</p>
                  </div>
                </div>

                <div className="bg-secondary/20 p-6 sm:p-8">
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-secondary-text">Live estimate</p>
                  <label className="mt-5 block text-sm font-medium text-text" htmlFor="custom-plan-employees">
                    Number of employees
                  </label>
                  <input
                    id="custom-plan-employees"
                    type="number"
                    min={201}
                    step={1}
                    className="input-field mt-2 w-full rounded-2xl px-4 py-3 text-base"
                    value={Math.max(201, Number(employeeCount) || 201)}
                    onChange={(e) => {
                      const nextCount = Math.max(201, parseInt(e.target.value, 10) || 201);
                      setSelectedTier('6');
                      setEmployeeCount(nextCount);
                      setBillingCycle('annual');
                    }}
                  />
                  <p className="mt-2 text-xs text-secondary-text">Tier 6 is yearly only and requires more than 200 employees.</p>

                  <div className="mt-6 rounded-[1.75rem] border border-border bg-white p-5 shadow-soft">
                    <p className="text-sm font-medium text-secondary-text">Estimated yearly price</p>
                    <p className="mt-2 text-4xl font-extrabold tracking-tight text-text">{tierSixEstimate}</p>
                    <p className="mt-3 text-sm text-secondary-text">{customTier.pricing.annualFormula}</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => selectPlan('6', Math.max(201, Number(employeeCount) || 201), 'annual')}
                    className="primary-btn mt-6 w-full justify-center py-3"
                  >
                    Select Custom Plan
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        <section ref={formRef} className="mt-10 grid gap-8 lg:grid-cols-[0.88fr_1.12fr]">
          <div className="space-y-6">
            <div className="soft-panel p-6 sm:p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-secondary-text">Plan enquiry</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-text">Talk to us about your rollout</h2>
              <p className="mt-3 text-base text-secondary-text">
                Select a plan above and share your details below. We will confirm fit, next steps, and onboarding guidance. No payment is collected here.
              </p>
            </div>

            {tier && selectedPlanSummary && (
              <div className="subtle-panel p-6">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-secondary-text">Selected plan</p>
                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-2xl font-bold text-text">{formatPlanDisplayName(tier)}</p>
                    <p className="mt-1 text-sm text-secondary-text">{formatEmployeeRange(tier.range)}</p>
                  </div>
                  <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs font-semibold text-primary">
                    {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}
                  </span>
                </div>
                <div className="mt-5 rounded-2xl border border-white/70 bg-white/80 p-4">
                  <p className="text-3xl font-extrabold tracking-tight text-text">{selectedPlanSummary.amount}</p>
                  <p className="mt-1 text-sm font-medium text-secondary-text">{selectedPlanSummary.cadence}</p>
                  <p className="mt-3 text-sm text-secondary-text">{selectedPlanSummary.secondary}</p>
                </div>
              </div>
            )}
          </div>

          <div className="card p-6 sm:p-8">
            {error && (
              <div className="alert-error mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3" role="alert" aria-live="assertive">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            {ok && (
              <div className="alert-success mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" role="status" aria-live="polite">
                <p className="text-sm text-emerald-700">{ok}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-text">Full Name</label>
                  <input
                    ref={fullNameInputRef}
                    className="input-field w-full rounded-2xl px-4 py-3"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-text">Organisation</label>
                  <input
                    className="input-field w-full rounded-2xl px-4 py-3"
                    value={organisation}
                    onChange={(e) => setOrganisation(e.target.value)}
                    placeholder="Acme Pvt. Ltd."
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-text">Designation (optional)</label>
                  <input
                    className="input-field w-full rounded-2xl px-4 py-3"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    placeholder="Operations Manager"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-text">Email</label>
                  <input
                    type="email"
                    className="input-field w-full rounded-2xl px-4 py-3"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-text">Contact Number</label>
                <div className="grid gap-3 sm:grid-cols-[0.9fr_2.1fr]">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="input-field w-full rounded-2xl bg-white px-4 py-3"
                  >
                    <option value="+91">+91 (IN)</option>
                    <option value="+1">+1 (US)</option>
                    <option value="+44">+44 (UK)</option>
                    <option value="+61">+61 (AU)</option>
                    <option value="+971">+971 (AE)</option>
                  </select>
                  <input
                    type="tel"
                    className="input-field w-full rounded-2xl px-4 py-3"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    placeholder="9876543210"
                    required
                  />
                </div>
                <p className="mt-2 text-xs text-secondary-text">Digits only. We will prepend your selected country code.</p>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-text">Plan</label>
                  <select
                    className="input-field w-full rounded-2xl px-4 py-3"
                    value={selectedTier}
                    onChange={handleTierChange}
                    disabled={tiersLoading || subscriptionTiers.length === 0}
                  >
                    {tiersLoading && <option value="">Loading plans...</option>}
                    {!tiersLoading && subscriptionTiers.length === 0 && <option value="">No plans available</option>}
                    {subscriptionTiers.map((item) => (
                      <option key={item.id} value={item.id}>
                        {formatPlanDisplayName(item)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-text">Billing</label>
                  <select
                    className="input-field w-full rounded-2xl px-4 py-3"
                    value={billingCycle}
                    onChange={handleBillingChange}
                    disabled={!tier || availableBillingCycles.length === 0}
                  >
                    {!tier && <option value="">Select a plan first</option>}
                    {tier && availableBillingCycles.length === 0 && <option value="">No billing options available</option>}
                    {availableBillingCycles.map((cycle) => (
                      <option key={cycle.id} value={cycle.id}>
                        {cycle.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-text">Employees</label>
                <input
                  type="number"
                  className="input-field w-full rounded-2xl px-4 py-3"
                  value={employeeCount}
                  min={tier?.range[0] || 1}
                  max={tier?.range[1] || undefined}
                  step={1}
                  inputMode="numeric"
                  onChange={handleEmployeeCountChange}
                  onBlur={handleEmployeeCountBlur}
                  required
                  disabled={!tier}
                />
                {tier && !withinRange(Number(employeeCount), tier.range) && (
                  <p className="mt-2 text-xs text-red-700">
                    Please enter a value within {tier.range[0]}{tier.range[1] ? `-${tier.range[1]}` : '+'}.
                  </p>
                )}
              </div>

              <div className="rounded-[1.75rem] border border-border bg-background/80 p-5">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-secondary-text">Before you submit</p>
                <ul className="mt-3 space-y-2 text-sm text-secondary-text">
                  <li>Selected plan details are populated from the pricing section above.</li>
                  <li>Tier 6 remains yearly only.</li>
                  <li>No payment is collected on this page.</li>
                </ul>
              </div>

              <div className="flex justify-end">
                <button type="submit" disabled={!tier || tiersLoading} className="primary-btn w-full justify-center rounded-2xl px-6 py-3 sm:w-auto">
                  Submit Enquiry
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
}
